import { Navigate, useSearch } from '@tanstack/react-router';

import { useCheckoutSessionStatus } from 'src/api/queries';
import { Spinner } from 'src/components';

/**
 * Stripe returns the customer here after a redirect-based payment (e.g.
 * PayPal). The checkout session id and country are passed along in the URL
 * so we can re-read the payment outcome and forward the customer to the
 * confirmation page on success, or back to checkout with an error on
 * failure.
 */
export const CheckoutReturnPage = () => {
  // `strict: false` (rather than a route-specific `getRouteApi`) matches the
  // convention used elsewhere in checkout V2 (see Page.tsx) and keeps this
  // component renderable in tests without needing to reconstruct the exact
  // route tree.
  const search = useSearch({ strict: false });
  const country = typeof search.country === 'string' ? search.country : '';
  const sessionId =
    typeof search.session_id === 'string' ? search.session_id : '';
  const checkoutSession = useCheckoutSessionStatus({ country, sessionId });

  // The session may still be `open` for a few seconds after the customer
  // returns while Stripe processes the webhook. `useCheckoutSessionStatus`
  // polls in that case; keep the spinner up until the status settles or
  // polling gives up.
  if (checkoutSession.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // If we cannot get the result, assume failure. The customer will be
  // redirected to the subscriptions page (via the checkout route) if the
  // payment actually succeeded.
  if (!checkoutSession.isSuccess) {
    return (
      <Navigate
        to="/store/checkout"
        search={{ redirect_status: 'failed' }}
        replace
      />
    );
  }

  const { status, paymentStatus } = checkoutSession.data;
  if (status !== 'complete' || paymentStatus === 'unpaid') {
    return (
      <Navigate
        to="/store/checkout"
        search={{ redirect_status: 'failed' }}
        replace
      />
    );
  }

  return <Navigate to="/store/confirmation" replace />;
};
